import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Users, Plus, Search, Phone, Mail, Calendar, MessageSquare,
  TrendingUp, Target, UserCheck, ChevronDown, ChevronUp,
  Loader2, ArrowRight, Clock, Send, Tag, Filter, BarChart3
} from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalesManagerTab = ({ auth, authAxios }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("overview");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterExec, setFilterExec] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [followUpNote, setFollowUpNote] = useState("");
  const [followUpType, setFollowUpType] = useState("note");
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [newLead, setNewLead] = useState({
    name: "", phone: "", email: "", source: "manual",
    occasion: "", preferred_package: "", preferred_date: "", notes: "", assigned_to: ""
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/sales/dashboard`);
      setDashboard(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const assignLead = async (leadId, execUsername) => {
    await authAxios.put(`${API_URL}/api/admin/leads/${leadId}/assign`, { assigned_to: execUsername });
    await fetchDashboard();
  };

  const updateLeadStatus = async (leadId, status) => {
    await authAxios.put(`${API_URL}/api/admin/leads/${leadId}/status`, { status });
    await fetchDashboard();
  };

  const addFollowUp = async (leadId) => {
    if (!followUpNote.trim()) return;
    await authAxios.post(`${API_URL}/api/admin/leads/${leadId}/follow-up`, { note: followUpNote, type: followUpType });
    setFollowUpNote("");
    await fetchDashboard();
  };

  const createLead = async () => {
    if (!newLead.name && !newLead.phone) return;
    await authAxios.post(`${API_URL}/api/admin/leads`, newLead);
    setNewLead({ name: "", phone: "", email: "", source: "manual", occasion: "", preferred_package: "", preferred_date: "", notes: "", assigned_to: "" });
    setShowCreateLead(false);
    await fetchDashboard();
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" size={32} /></div>;
  if (!dashboard) return <p className="text-center text-[#475569] py-8">Failed to load sales dashboard</p>;

  const { stats, leads, executives, sales_execs } = dashboard;

  const filteredLeads = (leads || []).filter(l => {
    const matchSearch = !searchTerm ||
      (l.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (l.phone || "").includes(searchTerm) ||
      (l.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || (l.status || "new") === filterStatus;
    const matchExec = filterExec === "all" || l.assigned_to === filterExec;
    return matchSearch && matchStatus && matchExec;
  });

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    assigned: "bg-indigo-100 text-indigo-700",
    contacted: "bg-yellow-100 text-yellow-700",
    qualified: "bg-purple-100 text-purple-700",
    converted: "bg-green-100 text-green-700",
    lost: "bg-red-100 text-red-700",
  };

  return (
    <div className="space-y-6" data-testid="sales-manager-tab">
      {/* Sub-navigation */}
      <div className="flex gap-2">
        {[
          { key: "overview", label: "Overview", icon: BarChart3 },
          { key: "leads", label: "All Leads", icon: Users, count: stats.total_leads },
          { key: "performance", label: "Team Performance", icon: TrendingUp },
        ].map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeView === v.key ? 'bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white shadow-md' : 'bg-white text-[#475569] hover:bg-[#FFFAF5]'}`}
            data-testid={`sales-view-${v.key}`}>
            <v.icon size={16} /> {v.label}
            {v.count > 0 && <span className="px-1.5 py-0.5 bg-white/30 rounded-full text-xs font-bold">{v.count}</span>}
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#E83E8C]">{stats.total_leads}</p>
          <p className="text-sm text-[#475569]">Total Leads</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-blue-500">{stats.new_leads}</p>
          <p className="text-sm text-[#475569]">New</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#FF9F1C]">{stats.unassigned_leads}</p>
          <p className="text-sm text-[#475569]">Unassigned</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#25D366]">{stats.converted_leads}</p>
          <p className="text-sm text-[#475569]">Converted</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-purple-500">{stats.conversion_rate}%</p>
          <p className="text-sm text-[#475569]">Conversion Rate</p>
        </div>
      </div>

      {/* OVERVIEW VIEW */}
      {activeView === "overview" && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Unassigned Leads (urgent) */}
          <div className="bg-white rounded-3xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-4 flex items-center gap-2">
              <Target size={18} className="text-[#FF9F1C]" /> Unassigned Leads ({stats.unassigned_leads})
            </h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(leads || []).filter(l => !l.assigned_to).slice(0, 10).map((l, i) => (
                <div key={l.id || `lead-${i}`} className="flex items-center gap-3 p-3 rounded-xl border border-orange-100 bg-orange-50/30">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1E1B4B] truncate">{l.name || l.phone || "Unknown"}</p>
                    <p className="text-xs text-[#94a3b8]">{l.source} | {l.created_at?.split("T")[0]}</p>
                  </div>
                  <select onChange={e => e.target.value && assignLead(l.id, e.target.value)} defaultValue=""
                    className="px-2 py-1 rounded-lg border text-xs focus:border-[#E83E8C] outline-none"
                    data-testid={`assign-select-${i}`}>
                    <option value="">Assign to...</option>
                    {(sales_execs || []).map(e => (
                      <option key={e.username} value={e.username}>{e.name || e.username}</option>
                    ))}
                  </select>
                </div>
              ))}
              {stats.unassigned_leads === 0 && <p className="text-sm text-[#94a3b8] text-center py-4">All leads are assigned!</p>}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-3xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-4 flex items-center gap-2">
              <Clock size={18} className="text-[#E83E8C]" /> Recent Leads
            </h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(leads || []).slice(0, 10).map((l, i) => (
                <div key={l.id || `recent-${i}`} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-[#FFFAF5]">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white font-bold text-xs">
                    {(l.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-xs text-[#1E1B4B] truncate">{l.name || l.phone || "Unknown"}</p>
                    <p className="text-xs text-[#94a3b8]">{l.source} | {l.phone}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[l.status || "new"]}`}>{l.status || "new"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LEADS VIEW */}
      {activeView === "leads" && (
        <div className="bg-white rounded-3xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2">
              <Users size={18} className="text-[#E83E8C]" /> All Leads
            </h3>
            <button onClick={() => setShowCreateLead(!showCreateLead)}
              className="px-4 py-2 bg-[#E83E8C] text-white rounded-xl text-sm font-medium flex items-center gap-2 hover:opacity-90"
              data-testid="create-lead-btn">
              <Plus size={14} /> New Lead
            </button>
          </div>

          {/* Create Lead Form */}
          {showCreateLead && (
            <div className="p-4 rounded-2xl bg-[#FFFAF5] border border-[#f0e6d9] mb-4 space-y-3" data-testid="create-lead-form">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <input value={newLead.name} onChange={e => setNewLead({...newLead, name: e.target.value})} placeholder="Name *"
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="new-lead-name" />
                <input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} placeholder="Phone *"
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="new-lead-phone" />
                <input value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} placeholder="Email"
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" />
                <select value={newLead.occasion} onChange={e => setNewLead({...newLead, occasion: e.target.value})}
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none">
                  <option value="">Occasion</option>
                  {["Birthday", "Anniversary", "Baby Shower", "Housewarming", "Engagement", "Other"].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <select value={newLead.preferred_package} onChange={e => setNewLead({...newLead, preferred_package: e.target.value})}
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none">
                  <option value="">Package</option>
                  {["Basic", "Classic", "Premium", "Luxury"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <input type="date" value={newLead.preferred_date} onChange={e => setNewLead({...newLead, preferred_date: e.target.value})}
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" />
              </div>
              <div className="flex gap-3 items-center">
                <select value={newLead.assigned_to} onChange={e => setNewLead({...newLead, assigned_to: e.target.value})}
                  className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none">
                  <option value="">Assign to...</option>
                  {(sales_execs || []).map(e => <option key={e.username} value={e.username}>{e.name || e.username}</option>)}
                </select>
                <input value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})} placeholder="Notes"
                  className="flex-1 px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" />
                <button onClick={createLead} className="px-4 py-2 bg-[#25D366] text-white rounded-xl text-sm font-medium" data-testid="save-new-lead">Save</button>
                <button onClick={() => setShowCreateLead(false)} className="px-4 py-2 bg-gray-100 text-gray-500 rounded-xl text-sm">Cancel</button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-48 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search leads..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="leads-search" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="leads-filter-status">
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="assigned">Assigned</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
            <select value={filterExec} onChange={e => setFilterExec(e.target.value)}
              className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="leads-filter-exec">
              <option value="all">All Executives</option>
              <option value="">Unassigned</option>
              {(sales_execs || []).map(e => <option key={e.username} value={e.username}>{e.name || e.username}</option>)}
            </select>
          </div>

          {/* Leads List */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredLeads.length === 0 ? (
              <p className="text-sm text-[#94a3b8] text-center py-8">No leads found matching your filters</p>
            ) : filteredLeads.map((l, i) => (
              <div key={l.id || i} className="rounded-xl border border-gray-100 hover:border-[#E83E8C]/30 transition-all" data-testid={`lead-row-${i}`}>
                <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpandedLead(expandedLead === l.id ? null : l.id)}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {(l.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1E1B4B] truncate">{l.name || "Unknown"}</p>
                    <p className="text-xs text-[#94a3b8] flex items-center gap-2">
                      {l.phone && <span className="flex items-center gap-1"><Phone size={10} /> {l.phone}</span>}
                      {l.email && <span className="flex items-center gap-1"><Mail size={10} /> {l.email}</span>}
                      <span>{l.source}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {l.occasion && <span className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-xs hidden sm:inline">{l.occasion}</span>}
                    {l.preferred_package && <span className="px-2 py-0.5 bg-[#FF9F1C]/10 text-[#FF9F1C] rounded-full text-xs hidden sm:inline">{l.preferred_package}</span>}
                    <select value={l.status || "new"} onChange={e => { e.stopPropagation(); updateLeadStatus(l.id, e.target.value); }}
                      className={`px-2 py-1 rounded-lg text-xs font-medium border-0 outline-none cursor-pointer ${statusColors[l.status || "new"]}`}
                      onClick={e => e.stopPropagation()}>
                      <option value="new">New</option>
                      <option value="assigned">Assigned</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>
                    <select value={l.assigned_to || ""} onChange={e => { e.stopPropagation(); assignLead(l.id, e.target.value); }}
                      className="px-2 py-1 rounded-lg border text-xs focus:border-[#E83E8C] outline-none"
                      onClick={e => e.stopPropagation()}>
                      <option value="">Unassigned</option>
                      {(sales_execs || []).map(e => <option key={e.username} value={e.username}>{e.name || e.username}</option>)}
                    </select>
                    {expandedLead === l.id ? <ChevronUp size={16} className="text-[#94a3b8]" /> : <ChevronDown size={16} className="text-[#94a3b8]" />}
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedLead === l.id && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {l.preferred_date && <div><span className="text-[#94a3b8] text-xs">Preferred Date</span><p className="font-medium text-[#1E1B4B]">{l.preferred_date}</p></div>}
                      {l.notes && <div className="col-span-2"><span className="text-[#94a3b8] text-xs">Notes</span><p className="text-[#1E1B4B]">{l.notes}</p></div>}
                      <div><span className="text-[#94a3b8] text-xs">Created</span><p className="font-medium text-[#1E1B4B]">{l.created_at?.split("T")[0]}</p></div>
                    </div>

                    {/* Follow-ups */}
                    {(l.follow_ups || []).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-[#1E1B4B] mb-2">Follow-up History</p>
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {(l.follow_ups || []).map((f, j) => (
                            <div key={f.at || `followup-${j}`} className="flex items-start gap-2 text-xs p-2 bg-gray-50 rounded-lg">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${f.type === 'call' ? 'bg-blue-100 text-blue-600' : f.type === 'whatsapp' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>{f.type || "note"}</span>
                              <span className="flex-1 text-[#1E1B4B]">{f.note}</span>
                              <span className="text-[#94a3b8] whitespace-nowrap">{f.by} • {f.at?.split("T")[0]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add Follow-up */}
                    <div className="flex gap-2 items-center">
                      <select value={followUpType} onChange={e => setFollowUpType(e.target.value)}
                        className="px-2 py-1.5 rounded-lg border text-xs focus:border-[#E83E8C] outline-none">
                        <option value="note">Note</option>
                        <option value="call">Call</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">Email</option>
                      </select>
                      <input value={followUpNote} onChange={e => setFollowUpNote(e.target.value)} placeholder="Add follow-up note..."
                        className="flex-1 px-3 py-1.5 rounded-lg border text-sm focus:border-[#E83E8C] outline-none"
                        onKeyDown={e => e.key === 'Enter' && addFollowUp(l.id)} data-testid="follow-up-input" />
                      <button onClick={() => addFollowUp(l.id)} className="px-3 py-1.5 bg-[#E83E8C] text-white rounded-lg text-xs font-medium" data-testid="add-follow-up-btn">Add</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PERFORMANCE VIEW */}
      {activeView === "performance" && (
        <div className="space-y-6">
          {Object.keys(executives || {}).length === 0 ? (
            <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
              <UserCheck size={40} className="mx-auto text-[#94a3b8] mb-3" />
              <p className="text-[#475569]">No sales executives added yet.</p>
              <p className="text-sm text-[#94a3b8] mt-1">Create sales executive accounts in the "Team & Roles" tab first.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {Object.values(executives).map((exec, i) => {
                const convRate = exec.total_leads > 0 ? Math.round(exec.converted / exec.total_leads * 100) : 0;
                return (
                  <div key={exec.username || `exec-${i}`} className="bg-white rounded-3xl shadow-lg p-6" data-testid={`exec-card-${exec.username || i}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {(exec.name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#1E1B4B] text-lg">{exec.name}</h3>
                        <p className="text-sm text-[#94a3b8]">@{exec.username}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="bg-blue-50 rounded-xl p-3">
                        <p className="text-xl font-bold text-blue-600">{exec.total_leads}</p>
                        <p className="text-xs text-[#475569]">Leads</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3">
                        <p className="text-xl font-bold text-green-600">{exec.converted}</p>
                        <p className="text-xs text-[#475569]">Converted</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-3">
                        <p className="text-xl font-bold text-purple-600">{convRate}%</p>
                        <p className="text-xs text-[#475569]">Conv Rate</p>
                      </div>
                      <div className="bg-[#FFFAF5] rounded-xl p-3">
                        <p className="text-xl font-bold text-[#E83E8C]">₹{(exec.revenue || 0).toLocaleString()}</p>
                        <p className="text-xs text-[#475569]">Revenue</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesManagerTab;
